// src/shared/dashboard/shell/statusChip.ts
//
// The account-status chip (Free / Trial / Paid / Expired), shared by the
// sidebar footer and the mobile top bar so both render the same state.
import { BadgeCheck, Clock3, Gift, Lock } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";
import { useAuth } from "@/shared/contexts/AuthContext";

export interface StatusChip {
  label: string;
  Icon: LucideIcon;
  className: string;
  /** True for the long "Free · {org} member" label, which tight bars may hide. */
  longLabel: boolean;
  /**
   * The account isn't known yet — render nothing rather than a verdict. While
   * auth is restoring, `user` is null and the subscription context reports
   * hasAccess=false, which renders as a red "Expired" chip. A sponsored cohort
   * student would see that flash on every cold load; it reads as a dead
   * account, which is the opposite of true.
   */
  pending: boolean;
}

export function useStatusChip(): StatusChip {
  const {
    subscription,
    daysRemaining,
    isTrialing,
    freeAccess,
    companyName,
    hasAccess,
    paywallEnabled,
    loading,
  } = useSubscription();
  const { loading: authLoading } = useAuth();
  // Order matters: an expired trial still has status "trialing" in the DB, so
  // hasAccess (server-computed) decides between "Trial · Nd" and "Expired".
  const chip = freeAccess
    ? {
        label: companyName ? `Free · ${companyName} member` : "Free access",
        Icon: Gift,
        className:
          "border border-[var(--line)] bg-[var(--paper-raised)] text-[var(--ink-secondary)]",
      }
    : paywallEnabled && !hasAccess
      ? {
          label: "Expired",
          Icon: Lock,
          className:
            "border border-[rgba(180,35,24,0.2)] bg-[var(--mark-attention-bg)] text-[var(--mark-attention)]",
        }
      : isTrialing
        ? {
            label: `Trial · ${daysRemaining}d`,
            Icon: Clock3,
            className:
              "border border-[rgba(26,77,88,0.25)] bg-[var(--mark-open-bg)] text-[var(--mark-open-fg)]",
          }
        : subscription?.status === "active" ||
            subscription?.status === "canceled"
          ? {
              label: "Paid",
              Icon: BadgeCheck,
              className:
                "border border-[rgba(8,126,107,0.2)] bg-[var(--mark-done-bg)] text-[var(--mark-done)]",
            }
          : {
              label: "Free",
              Icon: Gift,
              className:
                "border border-[var(--line)] bg-[var(--paper-raised)] text-[var(--ink-tertiary)]",
            };
  return {
    ...chip,
    longLabel: !!(freeAccess && companyName),
    pending: authLoading || loading,
  };
}
